/**
 * Dark mode hook.
 *
 * - Explicit choice persists to localStorage under `paz-theme`.
 * - With no stored choice the system `prefers-color-scheme` is followed and
 *   tracked live (the media-query listener only acts while source === 'system').
 * - `storage` events keep multiple tabs in sync.
 * - Nothing is written to storage on mount: a first visit must not freeze the
 *   system preference.
 *
 * `data-theme="dark"` on <html> is the single switch the CSS tokens key off.
 * The inline THEME_BOOT_SCRIPT (see lib/themeBoot.ts) applies it before first
 * paint; this hook keeps it in sync afterwards.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  THEME_BOOT_SCRIPT,
  THEME_STORAGE_KEY,
  applyTheme,
  resolveTheme,
  systemPrefersDark,
  writeStoredTheme,
  type ResolvedTheme,
  type ThemeMode,
  type ThemeSource,
} from '../lib/themeBoot';

export { THEME_BOOT_SCRIPT, THEME_STORAGE_KEY, type ThemeMode, type ThemeSource };

export interface UseDarkMode {
  isDark: boolean;
  /** 'stored' when the user chose explicitly, 'system' when following the OS. */
  source: ThemeSource;
  /** Flip the theme and persist the choice. */
  toggle: () => void;
  /** Set explicitly, or pass 'system' to clear the stored choice and follow the OS. */
  setTheme: (mode: ThemeMode | 'system') => void;
}

export function useDarkMode(): UseDarkMode {
  const [theme, setThemeState] = useState<ResolvedTheme>(() => resolveTheme());

  // Reflect state on <html>
  useEffect(() => {
    applyTheme(theme.isDark);
  }, [theme.isDark]);

  // Follow the OS while no explicit choice is stored
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setThemeState((prev) =>
        prev.source === 'system' ? { isDark: event.matches, source: 'system' } : prev,
      );
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Cross-tab sync
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
      setThemeState(resolveTheme());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((mode: ThemeMode | 'system') => {
    if (mode === 'system') {
      writeStoredTheme(null);
      setThemeState({ isDark: systemPrefersDark(), source: 'system' });
      return;
    }
    writeStoredTheme(mode);
    setThemeState({ isDark: mode === 'dark', source: 'stored' });
  }, []);

  const toggle = useCallback(() => {
    const next: ThemeMode = theme.isDark ? 'light' : 'dark';
    writeStoredTheme(next);
    setThemeState({ isDark: next === 'dark', source: 'stored' });
  }, [theme.isDark]);

  return { isDark: theme.isDark, source: theme.source, toggle, setTheme };
}
